import { useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import { GridCellProps } from "@progress/kendo-react-grid";
import { TbUserSearch } from "react-icons/tb";
import { fetchAuthenticatedData } from "@/queries/shared";
import { SearchResult } from "@/models";
import { CHLD } from "@/models/chld";
import { formatDateOnly } from "@/utils/dates";
import { useChildEntities, useCoreData } from "@/queries";
import { GlobalStateModel } from "@/models";
import Card from "@/components/shared/Card";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import SpinbuttonDateInput from "@/components/shared/forms/SpinbuttonDateInput";

type StateSearchFormState = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
};

const initialForm: StateSearchFormState = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
};

const formatGender = (gender: string | null | undefined): string => {
  if (!gender) return "";
  switch (gender.toUpperCase()) {
    case "M":
      return "Male";
    case "F":
      return "Female";
    case "X":
      return "Nonbinary";
    default:
      return gender;
  }
};

const CITY_STATE_COUNTRIES = new Set(["US", "CA", "MX"]);

const formatBirthLocation = (
  country: string | null | undefined,
  state: string | null | undefined,
  city: string | null | undefined,
  countryText: string
): string => {
  if (!country) return "";
  if (CITY_STATE_COUNTRIES.has(country.toUpperCase())) {
    const stateAbbr = state ? state.split("-").slice(1).join("-") : "";
    const parts = [city, stateAbbr].filter(Boolean);
    return parts.join(", ");
  }
  return countryText.toUpperCase();
};

const columns = [
  { field: "CSPPID", title: "CSPPID", width: "140px" },
  { field: "ChildName", title: "Child Name", width: "200px" },
  { field: "ChildDateOfBirth", title: "Birth Date", width: "120px" },
  { field: "BirthLocation", title: "Birth Location", width: "180px" },
  { field: "ChildGender", title: "Gender", width: "100px" },
  { field: "ReportingLEA", title: "LEA", width: "160px" },
  {
    field: "Score",
    title: "Match Score",
    width: "120px",
    cell: (props: GridCellProps) =>
      props.dataItem.Score != null ? (
        <td className={props.className} style={props.style}>
          {props.dataItem.Score.toFixed(2)}
        </td>
      ) : (
        <td className={props.className} style={props.style} />
      ),
  },
];

export function StateSearchView() {
  const leaContext = useRouteContext({ from: "/$leaid" }) as GlobalStateModel;
  const coreData = useCoreData();
  const leaList = useChildEntities("0-").data;

  const [formState, setFormState] = useState<StateSearchFormState>(initialForm);
  const [dobResetKey, setDobResetKey] = useState(0);
  const [dobIncomplete, setDobIncomplete] = useState(false);
  const [validationError, setValidationError] = useState<string>("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState<string>("");
  const [searchResults, setSearchResults] = useState<SearchResult<CHLD>[]>([]);

  const getCountryText = (code: string | null | undefined): string => {
    if (!code) return "";
    return coreData.countryList.find((c) => c.Value === code)?.Text ?? code;
  };

  const tableData = searchResults.map((r) => {
    const countryText = getCountryText(r.matchRecord.ChildCountryOfBirth);
    return {
      CSPPID: r.matchRecord.CSPPID || "",
      ChildName: [
        r.matchRecord.ChildFirstName,
        r.matchRecord.ChildMiddleName,
        r.matchRecord.ChildLastName,
      ]
        .filter(Boolean)
        .join(" "),
      ChildDateOfBirth: r.matchRecord.ChildDateOfBirth
        ? formatDateOnly(r.matchRecord.ChildDateOfBirth)
        : "",
      BirthLocation: formatBirthLocation(
        r.matchRecord.ChildCountryOfBirth,
        r.matchRecord.ChildStateOfBirth,
        r.matchRecord.ChildCityOfBirth,
        countryText
      ),
      ChildGender: formatGender(r.matchRecord.ChildGender),
      ReportingLEA:
        leaList?.find((x) => x.CDSCode === r.matchRecord.ReportingLEA)
          ?.LEAName || "",
      Score: r.score ?? null,
    };
  });

  const handleChange = (key: keyof StateSearchFormState, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    if (validationError) setValidationError("");
    if (searchError) setSearchError("");
  };

  const validate = (): boolean => {
    const hasFirst = formState.firstName.trim().length > 0;
    const hasLast = formState.lastName.trim().length > 0;
    const hasDOB = formState.dateOfBirth.trim().length === 10;

    if (formState.dateOfBirth && formState.dateOfBirth < "1900-01-01") {
      setValidationError("Please enter a valid Date of Birth.");
      return false;
    }
    if (hasFirst && hasLast && hasDOB) return true;

    if (dobIncomplete) {
      setValidationError("Please enter a complete Date of Birth.");
    } else {
      setValidationError(
        "Please enter First Name, Last Name, and Date of Birth."
      );
    }
    return false;
  };

  const handleSearch = async () => {
    if (!validate()) return;

    setIsLoading(true);
    setSearchError("");
    setHasSearched(true);

    try {
      const results = await fetchAuthenticatedData<SearchResult<CHLD>[]>(
        leaContext,
        `/json/child/search`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            CSPPID: "",
            ChildFirstName: formState.firstName,
            ChildLastName: formState.lastName,
            ChildDateOfBirth: formState.dateOfBirth,
          }),
        }
      );
      setSearchResults(results);
    } catch (err) {
      setSearchError(
        err instanceof Error
          ? err.message
          : "An error occurred. Please try again."
      );
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setFormState(initialForm);
    setDobResetKey((k) => k + 1);
    setDobIncomplete(false);
    setValidationError("");
    setSearchError("");
    setHasSearched(false);
    setSearchResults([]);
  };

  return (
    <>
      <Card className="card-custom mb-5 shadow-none">
        <div className="mb-0 p-4 bg-white w-100 child-search-form">
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <label
                id="state-search-first-name-label"
                htmlFor="state-search-first-name"
                className="form-label fw-semibold"
              >
                <i className="bi bi-person me-1" aria-hidden="true" /> Child
                First Name
              </label>
              <Input
                id="state-search-first-name"
                ariaLabelledBy="state-search-first-name-label"
                className="child-search-control"
                style={{ width: "100%" }}
                value={formState.firstName}
                onChange={(e: InputChangeEvent) =>
                  handleChange("firstName", e.value as string)
                }
                placeholder="Enter first name"
                disabled={isLoading}
              />
            </div>

            <div className="col-12 col-md-4">
              <label
                id="state-search-last-name-label"
                htmlFor="state-search-last-name"
                className="form-label fw-semibold"
              >
                <i className="bi bi-person me-1" aria-hidden="true" /> Child
                Last Name
              </label>
              <Input
                id="state-search-last-name"
                ariaLabelledBy="state-search-last-name-label"
                className="child-search-control"
                style={{ width: "100%" }}
                value={formState.lastName}
                onChange={(e: InputChangeEvent) =>
                  handleChange("lastName", e.value as string)
                }
                placeholder="Enter last name"
                disabled={isLoading}
              />
            </div>

            <div className="col-12 col-md-4">
              <label
                id="state-search-dob-label"
                className="form-label fw-semibold"
              >
                <i className="bi bi-calendar-event me-1" aria-hidden="true" />{" "}
                Date of Birth
                <span className="visually-hidden"> format mm/dd/yyyy</span>
              </label>
              <SpinbuttonDateInput
                key={dobResetKey}
                id="state-search-dob"
                labelledBy="state-search-dob-label"
                className="child-search-control"
                style={{ width: "100%" }}
                placeholderColor="#9ca3af"
                fontSize="14px"
                value={formState.dateOfBirth}
                onChange={(value) => handleChange("dateOfBirth", value)}
                onIncompleteChange={setDobIncomplete}
                disabled={isLoading}
                max="9999-12-31"
              />
            </div>

            {(validationError || searchError) && (
              <div className="col-12">
                <div
                  className="alert mb-0"
                  style={{
                    background: validationError ? "#fff3cd" : "#f8d7da",
                    border: `1px solid ${validationError ? "#ffc107" : "#f5c2c7"}`,
                    color: validationError ? "#856404" : "#842029",
                    borderRadius: "4px",
                    fontSize: "14px",
                    padding: "12px 16px",
                  }}
                >
                  {validationError || searchError}
                </div>
              </div>
            )}

            <div className="col-12 d-flex align-items-end gap-2 mt-4 mb-1">
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={handleSearch}
                disabled={isLoading}
              >
                <i className="bi bi-search" aria-hidden="true" />
                {isLoading ? "Searching..." : "Search"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClear}
                disabled={isLoading}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </Card>

      {!hasSearched ? (
        <Card className="card-custom shadow-none">
          <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "#e9ecef",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
            >
              <TbUserSearch size={30} color="#6c757d" />
            </div>
            <h5 className="fw-semibold mb-2" style={{ color: "#0d2d4a" }}>
              Search for Child Records
            </h5>
            <p
              className="mb-0"
              style={{ maxWidth: 380, fontSize: "0.9rem", color: "#212529" }}
            >
              Enter a child's name and date of birth above, then click Search to
              find matching records across the state.
            </p>
          </div>
        </Card>
      ) : isLoading ? (
        <Card className="card-custom shadow-none">
          <div className="d-flex align-items-center justify-content-center py-5 gap-3">
            <div
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
              style={{ color: "#0d2d4a" }}
            />
            <span style={{ color: "#0d2d4a" }}>Searching...</span>
          </div>
        </Card>
      ) : (
        <Card className="table-style mb-3">
          <KendoTableClientState
            data={tableData}
            columns={columns}
            filterable={false}
            noItemsMessage="No matching children found"
            initialTake={tableData.length || 10}
          />
        </Card>
      )}
    </>
  );
}
